/**
 * Demo accounts and organizations.
 *
 * Lives in `shared` because three separate things must agree on these values: the seed
 * script that creates the Auth users and Firestore documents, the sign-in screen that
 * offers one-click access to them, and the synthetic data generator that assigns
 * patients to orgs and care teams. When those three disagree, the symptom is a demo
 * where a clinician signs in successfully and sees an empty panel — technically working,
 * completely useless.
 *
 * These credentials are intentionally weak and intentionally public. They exist only in
 * the local emulator against synthetic data. A deployment to a real project must not
 * seed them, which `seedDemoData` enforces by refusing to run outside the emulator.
 */
import type { Role } from './vocab.js'

/**
 * The shared password for every seeded account. Deliberately not spelled with the word
 * "demo" in it: this value is rendered directly on the sign-in screen so a reviewer can
 * copy it, and the investor-facing build is trying to look like a real, working product
 * rather than a labeled prototype (see docs/DECISIONS.md). It is still, obviously, a
 * publicly-known password for synthetic accounts only.
 */
export const DEMO_PASSWORD = 'Rescure!2026'

export interface DemoOrg {
  id: string
  name: string
  type: 'SPONSOR' | 'CRO' | 'HEALTH_SYSTEM' | 'ACADEMIC_CENTER' | 'REGISTRY'
  city: string
  state: string
  lat: number
  lng: number
}

/**
 * Demo organizations.
 *
 * Deliberately spread across the country and across sizes. Two things depend on this
 * spread: the site-distance feature needs real geographic variation to be anything other
 * than a constant, and the feasibility view is only interesting if different sites return
 * genuinely different counts. A single-city demo makes both features look broken.
 *
 * The rural site exists specifically to exercise the equity story — it is far from
 * everything, which is exactly the situation that keeps rural patients out of trials.
 */
export const DEMO_ORGS: readonly DemoOrg[] = [
  {
    id: 'org_harborview',
    name: 'Harborview Health System',
    type: 'HEALTH_SYSTEM',
    city: 'Boston',
    state: 'MA',
    lat: 42.3601,
    lng: -71.0589,
  },
  {
    id: 'org_lakeside',
    name: 'Lakeside Medical Center',
    type: 'HEALTH_SYSTEM',
    city: 'Chicago',
    state: 'IL',
    lat: 41.8781,
    lng: -87.6298,
  },
  {
    id: 'org_cascade',
    name: 'Cascade Regional Health',
    type: 'HEALTH_SYSTEM',
    city: 'Seattle',
    state: 'WA',
    lat: 47.6062,
    lng: -122.3321,
  },
  {
    id: 'org_pinebluff',
    name: 'Pine Bluff Rural Clinic Network',
    type: 'HEALTH_SYSTEM',
    city: 'Pine Bluff',
    state: 'AR',
    lat: 34.2284,
    lng: -92.0032,
  },
  {
    id: 'org_meridian',
    name: 'Meridian Therapeutics',
    type: 'SPONSOR',
    city: 'Cambridge',
    state: 'MA',
    lat: 42.3736,
    lng: -71.1097,
  },
  {
    id: 'org_northstar',
    name: 'Northstar Clinical Research',
    type: 'CRO',
    city: 'Raleigh',
    state: 'NC',
    lat: 35.7796,
    lng: -78.6382,
  },
]

export interface DemoAccount {
  email: string
  password: string
  displayName: string
  role: Role
  orgId?: string
  /** Shown on the sign-in screen so a reviewer knows what this account demonstrates. */
  demoNote: string
  hasHumanSubjectsTraining: boolean
  specialty?: string
  /** For the patient account: which synthetic patient record it is linked to. */
  linkedPatientIndex?: number
}

/**
 * One account per role, so every permission path in the matrix is reachable from the
 * sign-in screen without editing a database.
 *
 * The two accounts worth understanding:
 *
 *  - `analyst@meridian.example` is the privacy demonstration. It is a sponsor-side role
 *    with full feasibility access and *no* ability to see a name, an age, or a criterion
 *    table. Signing in as it and trying to reach a patient record is the fastest way to
 *    see that the boundary is real rather than cosmetic.
 *  - `untrained@harborview.example` exists to demonstrate the training gate: identical
 *    role to the coordinator, but `hasHumanSubjectsTraining: false`, so the
 *    "approve outreach" action is refused. Role says what job you do; training says
 *    whether you may contact a human subject.
 */
export const DEMO_ACCOUNTS: readonly DemoAccount[] = [
  {
    email: 'patient@example.com',
    password: DEMO_PASSWORD,
    displayName: 'Rowan Alderisi',
    role: 'PATIENT',
    demoNote: 'A patient with metastatic NSCLC. Sees plain-language matches, no percentages.',
    hasHumanSubjectsTraining: false,
    linkedPatientIndex: 0,
  },
  {
    email: 'clinician@harborview.example',
    password: DEMO_PASSWORD,
    displayName: 'Dr Imani Okonkwo-Vale',
    role: 'CLINICIAN',
    orgId: 'org_harborview',
    specialty: 'Medical oncology',
    demoNote: 'A referring oncologist. Sees only their own panel, with per-criterion evidence.',
    hasHumanSubjectsTraining: false,
  },
  {
    email: 'pi@harborview.example',
    password: DEMO_PASSWORD,
    displayName: 'Dr Søren Whitfield-Nakamura',
    role: 'INVESTIGATOR',
    orgId: 'org_harborview',
    specialty: 'Cardiology',
    demoNote: 'A principal investigator: their panel plus their site’s pipeline and trial setup.',
    hasHumanSubjectsTraining: true,
  },
  {
    email: 'coordinator@harborview.example',
    password: DEMO_PASSWORD,
    displayName: 'Beatriz Quintanilla-Fry',
    role: 'ORG_COORDINATOR',
    orgId: 'org_harborview',
    demoNote: 'The study coordinator who does the actual screening. Works the candidate queue.',
    hasHumanSubjectsTraining: true,
  },
  {
    email: 'untrained@harborview.example',
    password: DEMO_PASSWORD,
    displayName: 'Devon Marchetti-Bloom',
    role: 'ORG_COORDINATOR',
    orgId: 'org_harborview',
    demoNote: 'Same role as the coordinator, but no human-subjects training — outreach is refused.',
    hasHumanSubjectsTraining: false,
  },
  {
    email: 'analyst@meridian.example',
    password: DEMO_PASSWORD,
    displayName: 'Priya Raghunathan-Oyelaran',
    role: 'ORG_ANALYST',
    orgId: 'org_meridian',
    demoNote: 'Sponsor feasibility analyst. Counts only — cannot reach a name or a chart quote.',
    hasHumanSubjectsTraining: true,
  },
  {
    email: 'admin@meridian.example',
    password: DEMO_PASSWORD,
    displayName: 'Tobias Lindqvist-Amara',
    role: 'ORG_ADMIN',
    orgId: 'org_meridian',
    demoNote: 'Sponsor admin: manages trials, users, and the audit log for Meridian.',
    hasHumanSubjectsTraining: true,
  },
  {
    email: 'platform@rescure.example',
    password: DEMO_PASSWORD,
    displayName: 'Wren Castellanos-Ib',
    role: 'PLATFORM_ADMIN',
    orgId: 'org_meridian',
    demoNote: 'Platform operator. Note: still cannot read patient identities, by design.',
    hasHumanSubjectsTraining: true,
  },
]

/** Health-system orgs, which are the ones that hold patient records. */
export const DEMO_HEALTH_SYSTEMS: readonly DemoOrg[] = DEMO_ORGS.filter((o) => o.type === 'HEALTH_SYSTEM')

/** Accounts grouped by the area they land in, for the sign-in screen's three columns. */
export function demoAccountsByArea(): Record<'patient' | 'clinician' | 'org', DemoAccount[]> {
  return {
    patient: DEMO_ACCOUNTS.filter((a) => a.role === 'PATIENT'),
    clinician: DEMO_ACCOUNTS.filter((a) => a.role === 'CLINICIAN' || a.role === 'INVESTIGATOR'),
    org: DEMO_ACCOUNTS.filter((a) =>
      ['ORG_COORDINATOR', 'ORG_ANALYST', 'ORG_ADMIN', 'PLATFORM_ADMIN'].includes(a.role)
    ),
  }
}
